import { Box, Chip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { fetchWebSocketApi } from "../../../api/webSocket";
import { LoadBoundary } from "../../brand/LoadBoundary";
import { loadKey, loadStatus } from "../../../hook/loadStatus";
import { requireRecord, useTrackedLoad } from "../../../hook/loadTracking";
import { useLoadError } from "../../../slices/ui";
import { useAppIdMap } from "../../../hook/app";
import { WebSocketRecord } from "../../../types/webSocket";
import { WebSocketBlock } from "../../../types/notebook/note/block/webSocket";
import { NoteBlockContainer } from "../../note";
import { CopyClipboard } from "../../copyClipboard";

// Shows the block's address and handler. The record is kept in component state: this view only reads it.
export const WebSocketBlockComponent: React.FC<{
  block: WebSocketBlock;
}> = React.memo(({ block }) => {
  const { "websocket-id": webSocketId } = block;
  const { appId } = useAppIdMap();
  const [webSocket, setWebSocket] = useState<WebSocketRecord | undefined>(undefined);
  const trackLoad = useTrackedLoad();
  const key = webSocketId ? loadKey("websocket", webSocketId) : undefined;
  const error = useLoadError(key);
  const status = loadStatus(webSocket, error);

  const load = () => {
    if (!webSocketId || !appId || !key) return;
    trackLoad(key, requireRecord(fetchWebSocketApi({ "websocket-id": webSocketId, "app-id": appId }), "WebSocket")).then((record) => {
      if (record !== undefined) setWebSocket(record);
    });
  };

  useEffect(() => {
    load();
  }, [webSocketId, appId]);

  const url = webSocket?.url;

  return (
    <NoteBlockContainer name="WebSocket">
      <LoadBoundary
        status={status}
        onRetry={load}
        label="Loading WebSocket"
        errorMessage="Could not load this WebSocket."
        testId="websocket-loading"
      >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">URL</Typography>
          {url ? (
            <>
              <Chip label={url} size="small" variant="outlined" />
              <CopyClipboard value={url} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Not registered yet</Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Handler</Typography>
          <Typography variant="body2">
            {webSocket ? `${webSocket["proto-name"]}/${webSocket["method-name"]}` : "—"}
          </Typography>
        </Box>
      </Box>
      </LoadBoundary>
    </NoteBlockContainer>
  );
});
